import { useState } from "react";
import { StyleSheet } from "react-native";

import { useContentResource, useTracker } from "@ctv/core";
import { Button, Card, Text, Token } from "@ctv/shared-core/src";
import Snackbar from "@ctv/shared/contexts/Snackbar";

import IcSystemArrowRight12 from "@traveloka/icon-kit-web/react/IcSystemArrowRight12";

import { Content } from "@ctv/shared/components/Layout";
import StepHeader from "@ctv/shared/components/Layout/StepHeader";
import { usePreRegisterConfirm } from "../_contexts/PreRegisterConfirmContext";
import { RegistrationStatus } from "../types";

import ActivateAccount from "./AfterSubmission/ActivateAccount";
import RegistrationSubmitted from "./AfterSubmission/RegistrationSubmitted";
import VerificationSteps from "./AfterSubmission/VerificationSteps";
import RegistrationStep from "./RegistrationStep";
import RequiredDocumentModal from "./RequiredDocumentModal";
import SendEmailForm from "./SendEmailForm";

export type ProcessStep =
  | "VERIFICATION_PROCESS"
  | "COMPLETED"
  | "REJECTED"
  | "INITIAL_PROCESS";

export default function RegistrationProcess() {
  const [isRequiredDocumentVisible, setIsRequiredDocumentVisible] =
    useState(false);

  const data = usePreRegisterConfirm();

  const { status } = data ?? {};

  const cr = useContentResource().CorporateRegistration;
  const track = useTracker("ors-page");

  const currentStep = getCurrentStep(status);

  return (
    <>
      <StepHeader current={-1} steps={[]} />
      <Snackbar />
      <Content style={styles.content}>
        <Text variant="headline" style={styles.headline}>
          {cr.processOverviewV2}
        </Text>
        <Text ink="black-secondary" style={styles.subHeadline}>
          {cr.processOverviewHelpV3}
        </Text>
        <RegistrationStep
          step={1}
          style={styles.step}
          title={cr.registrationPhaseV2}
        >
          {currentStep === "INITIAL_PROCESS" ? (
            <Card style={styles.cardContent}>
              <Text variant="ui-small">{cr.registrationPhaseHelpV3}</Text>
              <Button
                style={styles.seeRequiredDocumentButton}
                endIcon={
                  <IcSystemArrowRight12
                    color={Token.color.bluePrimary}
                    accentColor={Token.color.bluePrimary}
                    width={12}
                    height={12}
                  />
                }
                text={cr.seeRequiredDocumentsButton}
                variant="text-primary"
                size="small"
                onPress={() => {
                  setIsRequiredDocumentVisible(true);
                  track("corpB2b.onlineRegistration.webEvent", {
                    eventName: "CORP_B2B.ONLINE_REGISTRATION.WEB_EVENT",
                    eventVersion: "1.0.0",
                    pageName: "ORS_PAGE",
                    pageCategory: "ORS_PAGE",
                    eventValue: "CLICK_REQUIRED_DOCUMENT",
                  }).send();
                }}
              />
              <SendEmailForm />
            </Card>
          ) : (
            <RegistrationSubmitted />
          )}
        </RegistrationStep>
        <RegistrationStep
          step={2}
          style={styles.step}
          title={cr.verificationPhaseV2}
        >
          {currentStep === "INITIAL_PROCESS" ? (
            <Card style={styles.cardContent}>
              <Text variant="ui-small">{cr.verificationPhaseHelpV3}</Text>
            </Card>
          ) : (
            <VerificationSteps status={currentStep} />
          )}
        </RegistrationStep>
        <RegistrationStep
          step={3}
          style={styles.step}
          title={cr.accountCreatedPhaseV2}
          disabled={currentStep === "REJECTED"}
        >
          {currentStep === "COMPLETED" ? (
            <ActivateAccount />
          ) : (
            <Card style={styles.cardContent}>
              <Text
                variant="ui-small"
                ink={
                  currentStep === "REJECTED"
                    ? "black-secondary"
                    : "black-primary"
                }
              >
                {cr.accountCreatedPhaseHelpV3}
              </Text>
            </Card>
          )}
        </RegistrationStep>
      </Content>
      <RequiredDocumentModal
        isVisible={isRequiredDocumentVisible}
        onClose={() => setIsRequiredDocumentVisible(false)}
      />
    </>
  );
}

const styles = StyleSheet.create({
  headline: {
    marginBottom: Token.spacing.m,
  },
  subHeadline: {
    marginBottom: Token.spacing.xxl,
  },
  content: {
    width: 640,
  },
  cardContent: {
    padding: Token.spacing.l,
  },
  registrationCompleteContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: Token.spacing.l,
  },
  registrationComplete: {
    marginLeft: Token.spacing.m,
  },
  registrationCompleteInfo: {
    marginTop: Token.spacing.l,
  },
  linkText: {
    textDecorationLine: "underline",
    color: Token.color.bluePrimary,
  },
  seeRequiredDocumentButton: {
    //@ts-expect-error fit-content is not in React Native type
    width: "fit-content",
    marginTop: Token.spacing.m,
    padding: 0,
  },
  sendEmail: {
    marginTop: Token.spacing.l,
    flexDirection: "row",
  },
  emailLabel: {
    marginTop: Token.spacing.l,
  },
  emailInput: {
    marginEnd: Token.spacing.m,
    flex: 1,
  },
  step: {
    marginBottom: Token.spacing.xxl,
  },
  verificationPhaseInfo: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: Token.spacing.m,
  },
  verificationPhaseInfoLabel: {
    marginLeft: Token.spacing.m,
  },
  verificationWaitLongerInfo: {
    marginTop: Token.spacing.m,
  },
  info: {
    marginBottom: Token.spacing.l,
  },
  signInButton: {
    marginLeft: "auto",
  },
});

function getCurrentStep(
  status: Nullable<RegistrationStatus> | undefined
): ProcessStep {
  switch (status) {
    case RegistrationStatus.NEED_VERIFICATION:
    case RegistrationStatus.ONGOING_CREDIT_ASSESSMENT:
    case RegistrationStatus.ONGOING_LEGAL_REVISION:
    case RegistrationStatus.AGREEMENT_SENT_TO_CLIENT:
    case RegistrationStatus.REQUEST_FOR_ACTIVATION:
      return "VERIFICATION_PROCESS";
    case RegistrationStatus.ACTIVE:
    case RegistrationStatus.REGISTERED:
      return "COMPLETED";
    case RegistrationStatus.REJECTED:
    case RegistrationStatus.REJECTED_BY_CORPORATE:
    case RegistrationStatus.REJECTED_BY_INTERNAL:
      return "REJECTED";
    default:
      return "INITIAL_PROCESS";
  }
}
